<!--
  File name: ExportDropdown.vue
  Program Description:
    - This component is the dropdown button used by the reports generation
    to export the data as a TSV or PDF.
-->
<template>
  <div>
    <v-menu
      v-model="menu"
      offset-y
      :close-on-content-click="false"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          :disabled="disabled"
          :loading="loading"
          color="primary"
          class="rounded-l-lg"
          tile
          v-bind="attrs"
          v-on="on"
        >
          <v-icon class="mr-2">
            mdi-download
          </v-icon>
          {{ buttonName }}
        </v-btn>
      </template>
      <v-list
        v-if="collection === 'dbn'"
        class="pa-0"
      >
        <v-list-item
          v-for="(item, index) in ['Report', 'Graph']"
          :key="index"
          class="dropdown-item pr-2 pl-7"
          :style="{
            borderBottom: 'thin solid rgba(0, 0, 0, 0.12)',
            cursor: 'pointer'
          }"
        >
          <v-menu
            :disabled="disabledDropdown(item)"
            open-on-hover
            offset-x
          >
            <template v-slot:activator="{ on, attrs }">
              <v-list-item-title
                class="d-flex justify-space-between"
                v-bind="attrs"
                v-on="on"
                @click="saveAs(item === 'Report' ? item : 'DBN per Cause')"
              >
                <span>
                  {{ item }}
                </span>
                <v-icon
                  v-if="!disabledDropdown(item)"
                  class="ml-2"
                >
                  mdi-chevron-right
                </v-icon>
              </v-list-item-title>
            </template>
            <v-list class="pa-0">
              <v-list-item
                v-for="exportAs, idx in hoveredItem(item)"
                :key="idx"
                class="dropdown-item"
                :style="{
                  borderBottom: 'thin solid rgba(0, 0, 0, 0.12)',
                  cursor: 'pointer'
                }"
                @click="saveAs(exportAs)"
              >
                <v-list-item-title>
                  <span>
                    {{ exportAs }}
                  </span>
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-list-item>
      </v-list>
      <v-list
        v-else
        class="pa-0"
      >
        <v-list-item
          v-for="(item, index) in ['Report', 'Graph']"
          :key="index"
          class="dropdown-item pr-2 pl-7"
          :style="{
            borderBottom: 'thin solid rgba(0, 0, 0, 0.12)',
            cursor: 'pointer'
          }"
        >
          <v-menu
            :disabled="disabledDropdown(item)"
            open-on-hover
            offset-x
          >
            <template v-slot:activator="{ on, attrs }">
              <v-list-item-title
                class="d-flex justify-space-between"
                v-bind="attrs"
                v-on="on"
                @click="saveAs(item)"
              >
                <span>
                  {{ item }}
                </span>
                <v-icon
                  v-if="!isExternalAsirExport"
                  class="ml-2"
                >
                  mdi-chevron-right
                </v-icon>
              </v-list-item-title>
            </template>
            <v-list
              v-if="!isExternalAsirExport"
              class="pa-0"
            >
              <v-list-item
                v-for="exportAs, idx in hoveredItem(item)"
                :key="idx"
                class="dropdown-item"
                :style="{
                  borderBottom: 'thin solid rgba(0, 0, 0, 0.12)',
                  cursor: 'pointer'
                }"
                @click="saveAs(exportAs, item)"
              >
                <v-list-item-title>
                  <span>
                    {{ exportAs }}
                  </span>
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-list-item>
      </v-list>
    </v-menu>
    <v-btn
      :disabled="disabled || loading"
      color="primary"
      class="rounded-r-lg px-0"
      style="margin-left: 2px"
      height="36"
      x-small
      tile
      @click="menu = !menu"
    >
      <v-icon width="10">
        mdi-menu-down
      </v-icon>
    </v-btn>
    <div class="hide-graph">
      <template v-if="collection === 'dbn'">
        <ExportGraph
          id="sector-graph"
          ref="sectorGraph"
          :items="toggledItems({a: sectorChartItems, b: subsectorGraphData})"
          :is-pie="isSecPie"
          :title="toggledLabelItems"
          report-title="Data Breach Notification Report"
        />
        <ExportGraph
          id="cause-graph"
          ref="causeGraph"
          :items="[causeChartItems]"
          :is-pie="isCausePie"
          :title="['Total no. of DBN per General Cause']"
          report-title="Data Breach Notification Report"
        />
      </template>
      <template v-else>
        <ExportGraph
          ref="exportAsirChart"
          is-asir
          :items="asirChartItems(roleChecker('pic') ? 'perYear' : 'perSector')"
          :is-pie="asirChartFormat"
          report-title="Annual Security Incident Report"
          :title="roleChecker('pic') ? ['How Incidents Occur per year'] : toggledLabelItems"
        />
        <ExportGraph
          ref="exportAsirPerOrg"
          is-asir
          is-pie
          :items="asirChartItems('perOrg')"
          report-title="Annual Security Incident Report"
          :title="['How Incidents Occur per year']"
        />
      </template>
    </div>
  </div>
</template>

<script>
import { mapFields } from 'vuex-map-fields'
import { monthName, capitalize } from '@/utils/helpers'
import ExportGraph from './ExportGraph.vue'
import exportMixin from '@/mixins/exportMixin'
export default {
  name: 'ExportDropdown',
  components: {
    ExportGraph
  },
  mixins: [exportMixin],
  props: {
    buttonName: {
      type: String,
      default: 'Export'
    },
    collection: {
      type: String,
      default: 'dbn'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    dataset: {
      type: Array,
      default: undefined
    },
    filteredData: {
      type: Array,
      default: undefined
    },
    filteredAsirData: {
      type: Object,
      default: undefined
    },
    chartFormat: {
      type: Array,
      default: undefined
    },
    selectedYears: {
      type: Array,
      default: () => []
    },
    sectorToggleValue: {
      type: Array,
      default: () => []
    }
  },
  data: () => ({
    menu: false,
    loading: false,
    chartItems: {},
    headerText: {
      dbnNo: 'DBN No.',
      sector: 'Sector',
      subsector: 'Subsector',
      year: 'Year',
      quarter: 'Quarter',
      month: 'Month',
      classification: 'Classification',
      notifType: 'Notification Type',
      status: 'Status',
      docType: 'Document Type',
      picOrganization: 'PIC',
      briefSummary: 'Brief Summary',
      withRequest: 'With Request',
      typeOfRequest: 'Type of Request'
    },
    asirHeaderText: {
      year: 'Year',
      organization: 'Organization',
      sector: 'Sector',
      subsector: 'Subsector',
      classification: 'Classification',
      theft: 'Theft',
      identityFraud: 'Identity Fraud',
      sabotage: 'Sabotage/Physical Damage',
      maliciousCode: 'Malicious Code',
      hacking: 'Hacking',
      misuseOfResources: 'Misuse of Resources',
      hardwareFailure: 'Hardware Failure',
      softwareFailure: 'Software Failure',
      communicationFailure: 'Communication Failure',
      naturalDisaster: 'Natural Disaster',
      designError: 'Design Error',
      userError: 'User Error',
      operationsError: 'Operations Error',
      softwareMaintenanceError: 'Software Maintenance Error',
      thirdParty: 'Third Party / Service Provider',
      mandatoryBreachReported: 'Mandatory breach reported',
      others: 'Others',
      specify: 'Specification of others'
    },
    asirSubmissionHeaderText: {
      organization: 'Organization',
      year: 'Year',
      isSubmitted: 'ASIR Submitted?'
    }
  }),
  computed: {
    ...mapFields('pdbnfGraph', [
      'sectorList'
    ]),
    isExternal () {
      if (!this.$s.userType) return
      return this.$s.userType === 'External'
    },
    isExternalAsirExport () {
      return this.isExternal && this.collection === 'asir'
    },
    hoveredItem () {
      return item => {
        if (item === 'Report') {
          return this.collection === 'asir' ? ['IR Per Sector', 'IR Per Organization'] : ['as PDF', 'as TSV']
        } else if (item === 'Graph') {
          return this.collection === 'dbn' ? ['DBN per Sector', 'DBN per Cause'] : ['IR Per Sector', 'Incidents Occur per year']
        }
      }
    },
    disabledDropdown () {
      return selectedItem => this.roleChecker('pic') && selectedItem === 'Graph'
    },
    userRole () {
      return this.$s && this.$s.userRole
    },
    roleChecker () {
      return role => this.userRole && this.userRole.includes(role)
    },
    sectorGraphElement () {
      return this.$el.querySelector('#sector-graph')
    },
    causeGraphElement () {
      return this.$el.querySelector('#cause-graph')
    },
    asirChartItems () {
      return type => {
        if (!this.dataset.length) return []
        if (type === 'perYear') return [this.dataset[0]]
        else if (type === 'perSector') return this.toggledItems({ a: this.dataset[1], b: this.dataset[3] })
        else if (type === 'perOrg') return [this.dataset[2]]
      }
    },
    toggledItems () {
      const populateItems = (itemA, itemB) => {
        const items = []
        this.sectorToggleValue.includes(0) && items.push(itemA)
        this.sectorToggleValue.includes(1) && items.push(itemB)
        return items
      }
      return ({ a, b }) => populateItems(a, b)
    },
    toggledLabelItems () {
      const items = []
      for (const value of this.sectorToggleValue) {
        const reportFor = value === 0 ? 'Sector' : 'Subsector'
        const reportName = this.collection === 'dbn' ? 'DBN' : 'Incident Report'
        items.push(`Total no. of ${reportName} per ${reportFor}`)
      }
      return items
    },
    subsectorGraphData () {
      return this.dataset.length && this.dataset[2]
    },
    asirChartFormat () {
      return this.chartFormat.length && this.chartFormat[0]
    },
    sectorChartItems () {
      return this.dataset && this.dataset.find((data) => data.datasets[0].label === 'Sectors')
    },
    isSecPie () {
      const type = this.chartFormat && this.chartFormat.find((data) => data.type === 'sector')
      return type && type.isPie
    },
    isCausePie () {
      const type = this.chartFormat && this.chartFormat.find((data) => data.type === 'general cause')
      return type && type.isPie
    },
    causeChartItems () {
      return this.dataset && this.dataset.find((data) => data.datasets[0].label === 'General Causes')
    },
    filteredAsirYear () {
      return this.filteredAsirData.filteredYear
    },
    filteredAsirs () {
      return this.filteredAsirData.filteredAsirs
    },
    modified () {
      if (this.collection !== 'dbn') return
      return this.filteredData.map(item => {
        const sectorId = item.pic.sector
        const subId = item.pic.subsector
        const { sectorName, subsectorName, classification } = this.sectorNames({ sectorId, subId })
        const dateCreated = new Date(item.statusHistory[0].date)
        const month = dateCreated.getMonth()
        const quarter = 'Q' + Math.floor((month + 3) / 3)
        const adminCols = {
          picOrganization: item.picOrganization,
          briefSummary: item.briefSummary,
          withRequest: item.withRequest,
          typeOfRequest: item.typeOfRequest.join(', ')
        }
        const additionalCols = this.roleChecker('admin') ? adminCols : {}
        return {
          dbnNo: item.dbnNumber,
          sector: sectorName,
          subsector: subsectorName,
          year: item.year,
          quarter,
          month: monthName(month),
          classification: capitalize(classification),
          notifType: item.notification,
          status: item.formattedStatus,
          docType: item.documentType,
          ...additionalCols
        }
      })
    },
    asirReports () {
      if (this.collection !== 'asir') return
      const toFilter = this.isExternal ? this.filteredAsirYear : this.filteredAsirs
      return toFilter.map(item => {
        const classification = item.sectorName.classification
        const incidents = item.securityIncidentsOccured
        return {
          year: item.year,
          organization: item.pic,
          sector: item.sectorName.value,
          subsector: item.subSectorName.length ? item.subSectorName[0].value : 'N/A',
          classification: classification.charAt(0).toUpperCase() + classification.slice(1),
          theft: incidents.theft,
          identityFraud: incidents.identityFraud,
          sabotage: incidents.sabotage,
          maliciousCode: incidents.maliciousCode,
          hacking: incidents.hacking,
          misuseOfResources: incidents.misuseOfResources,
          hardwareFailure: incidents.hardwareFailure,
          softwareFailure: incidents.softwareFailure,
          communicationFailure: incidents.communicationFailure,
          naturalDisaster: incidents.naturalDisaster,
          designError: incidents.designError,
          userError: incidents.userError,
          operationsError: incidents.operationsError,
          softwareMaintenanceError: incidents.softwareMaintenanceError,
          thirdParty: incidents.thirdParty,
          mandatoryBreachReported: item.summary.mandatoryBreachNotification,
          others: incidents.others,
          specify: incidents.specify ? incidents.specify : 'N/A'
        }
      })
    },
    asirSubmission () {
      if (this.collection !== 'asir' || !this.filteredAsirYear) return []
      const asirSubmission = []
      const maxYear = (new Date().getFullYear() - 1)
      const canSubmitYears = Array.from({ length: (maxYear - 2018) + 1 }, (_, i) => i + 2018)
      const organizations = [...new Set(this.filteredAsirYear.map(item => item.pic))]
      const years = this.selectedYears.length ? this.selectedYears : canSubmitYears
      for (const organization of organizations) {
        for (const year of years) {
          const submitted = [...this.filteredAsirYear].filter(item => item.pic === organization && parseInt(item.year) === year)
          asirSubmission.push({ organization, year, isSubmitted: submitted.length ? 'Yes' : 'No' })
        }
      }
      return asirSubmission
    },
    headers () {
      if (this.collection !== 'dbn') return
      if (this.modified.length === 0) return
      let valueHeader = Object.keys(this.modified[0])
      if (this.roleChecker('pic')) {
        const end = valueHeader.length - 2
        valueHeader = valueHeader.slice(0, end)
      }
      return valueHeader.map((value) => {
        const text = this.headerText[`${value}`]
        const cellClass = 'centeredTableRow'
        return { text, value, cellClass }
      })
    },
    asirReportHeaders () {
      if (this.collection !== 'asir') return
      if (this.asirReports.length === 0) return
      const valueHeader = Object.keys(this.asirReports[0])
      return valueHeader.map((value) => {
        const text = this.asirHeaderText[`${value}`]
        const cellClass = 'centeredTableRow'
        return { text, value, cellClass }
      })
    },
    asirSubmissionHeaders () {
      if (this.collection !== 'asir') return
      if (this.asirSubmission.length === 0) return
      const valueHeader = Object.keys(this.asirSubmission[0])
      return valueHeader.map((value) => {
        const text = this.asirSubmissionHeaderText[`${value}`]
        const cellClass = 'centeredTableRow'
        return { text, value, cellClass }
      })
    },
    sectorNames () {
      return ({ sectorId, subId }) => {
        const sectors = this.sectorList
        const item = sectors.filter(sector => sector._id === sectorId)
        if (!item.length) return {}
        const sectorName = item[0] && item[0].value
        const subsectorItem = item[0] && item[0].subsector.filter(sub => sub._id === subId)
        const subsectorName = subsectorItem[0] && subsectorItem[0].value
        const currSector = this.sectorList.find(i => i._id === sectorId)
        const classification = currSector.classification
        return { sectorName, subsectorName, classification }
      }
    }
  },
  methods: {
    // Save the data as TSV or PDF
    async saveAs (format, menu) {
      if ((!(this.dataset && this.dataset.length) && this.collection !== 'asir') || (format === 'Report' && !this.isExternalAsirExport)) return
      this.loading = true
      this.menu = false
      if (format === 'as TSV') {
        await this.exportAsTSV(this.modified, 'BNReport', this.headers)
      } else if (format === 'as PDF') {
        this.drawDataForm(this.headers, this.modified)
      } else if (format === 'DBN per Sector') {
        await this.$refs.sectorGraph.exportToPDF('BNReport')
      } else if (format === 'DBN per Cause') {
        await this.$refs.causeGraph.exportToPDF('BNReport')
      } else if (format === 'IR Per Sector' && menu === 'Report') {
        if (!this.asirChartItems('perSector')[0].labels.length) return this.triggerAlert('TSV')
        await this.exportAsTSV(this.asirReports, 'ASIRReport', this.asirReportHeaders)
      } else if (format === 'IR Per Organization' && menu === 'Report') {
        if (!this.asirChartItems('perOrg')[0].labels.length) return this.triggerAlert('TSV')
        await this.exportAsTSV(this.asirSubmission, 'ASIRSubmission', this.asirSubmissionHeaders)
      } else if (format === 'Graph') {
        if (!this.asirChartItems('perYear')[0].labels.length) return this.triggerAlert('PDF')
        await this.$refs.exportAsirChart.exportToPDF('ASIRReport')
      } else if (format === 'Report' && this.isExternalAsirExport) {
        if (!this.asirChartItems('perYear')[0].labels.length) return this.triggerAlert('TSV')
        await this.exportAsTSV(this.asirReports, 'ASIRReport', this.asirReportHeaders)
      } else if (format === 'IR Per Sector' && menu === 'Graph') {
        if (!this.asirChartItems('perSector')[0].labels.length) return this.triggerAlert('PDF')
        await this.$refs.exportAsirChart.exportToPDF('ASIRReport')
      } else if (format === 'Incidents Occur per year') {
        if (!this.asirChartItems('perOrg')[0].labels.length) return this.triggerAlert('PDF')
        await this.$refs.exportAsirPerOrg.exportToPDF('ASIRReport')
      }
      this.loading = false
    },
    // Triggers the alert modal
    triggerAlert (type) {
      const message = `Unable to export graph as ${type}. No data available.`
      this.$store.dispatch('showAlert', { message, type: 'error' })
      this.loading = false
    }
  }
}
</script>

<style scoped>
.dropdown-item {
  transition: 0.3s ease-in-out !important;
}
.dropdown-item:hover {
  background-color: rgba(0, 0, 0, 0.12) !important;
}
.hide-graph{
  position: absolute;
  top: -9999px;
  left:-9999px;
  z-index: -9999;
  visibility:hidden;
}
</style>
